<script setup lang="ts">
import { computed } from 'vue'

import {
  ExpectedGoalsLayer,
  getMouseEventCoordinatesInZone,
} from '@collector/mobile-feature-pitch-expected-goals'
import { Pitch } from '@collector/shared-feature-pitch'
import Sentry from '@collector/shared-plugin-sentry-vue'
import {
  Coordinates,
  getCoordinatesWithTeamSideChanged,
  soccerPitchConfig,
} from '@collector/shared-ui-domain'
import SoccerPitchSvg from '@collector/shared-ui-domain/assets/soccer/pitch.svg'
import ParticipantButton from '@mobile/components/ParticipantButton/ParticipantButton.vue'
import router from '@mobile/router'
import GroupTitle from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDetailsModal/GroupTitle.vue'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import ChangeSidesButton from '@mobile/views/Relation/Shared/SidesSwitchOverlay/ChangeSidesButton.vue'
import { SoccerRelationIoCBindings } from '@mobile/views/Relation/Sports/Soccer/SoccerRelationDependencies/SoccerRelationDependencies'

interface Props {
  linkedIncident: ProbableLinkedIncident
}

const props = defineProps<Props>()

// composables
const {
  sidesSwitched,
  probableLinkedFirstParticipant,
  probableLinkedSecondParticipant,
} = useEventInjections()

// data
const incidentParticipantTeamSide = props.linkedIncident.side || 'home'
const ioc = router.currentRoute.value.meta.ioc

if (!ioc) {
  throw new Error(
    'IoC Container has to be instantiated and injected into route meta before using sport configuration',
  )
}

// inject
const zonesMatrix = ioc.get(SoccerRelationIoCBindings.ExpectedGoalsZonesMatrix)

if (zonesMatrix.length === 0) {
  Sentry.captureException(new Error('Expected goals zones matrix is empty'))
}

// computed
const isFulfilled = computed(() => !!coordinates.value)

// model
const coordinates = defineModel<Coordinates | null>('coordinates', {
  default: null,
})

// methods
function onFieldClick(params: {
  event: MouseEvent
  targetElement: HTMLElement
}): void {
  const { event, targetElement } = params

  coordinates.value = getCoordinatesWithTeamSideChanged(
    getMouseEventCoordinatesInZone(event, targetElement, zonesMatrix[0].length),
    sidesSwitched.value,
  )
}
</script>

<template>
  <div class="mx-auto w-3/4 space-y-2">
    <div class="relative flex justify-center">
      <div>
        <GroupTitle
          name="Coordinates"
          important
          :isFulfilled
        />
      </div>
      <div class="absolute right-0">
        <ChangeSidesButton />
      </div>
    </div>

    <div class="space-y-1">
      <div class="flex space-x-2">
        <ParticipantButton
          class="pointer-events-none !mb-0 !h-9"
          :participant="probableLinkedFirstParticipant.getParticipant().value"
        />
        <ParticipantButton
          class="pointer-events-none !mb-0 !h-9"
          :participant="probableLinkedSecondParticipant.getParticipant().value"
        />
      </div>
    </div>
    <div class="bg-green-900 py-3.5">
      <Pitch
        v-bind="soccerPitchConfig"
        :pitchImageSrc="SoccerPitchSvg"
        @fieldClick="onFieldClick"
      >
        <template #underlays>
          <ExpectedGoalsLayer
            :incidentParticipantTeamSide
            :teamSidesChanged="sidesSwitched"
            :coordinates
            :zonesMatrix
          />
        </template>
      </Pitch>
    </div>
  </div>
</template>
